import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Workout, ExerciseBlock } from '@/types/workout';
import { ExerciseReadDTO } from '@/types/exercise';
import { exerciseApi } from '@/services/exerciseApi';
import { workoutApi } from '@/services/workoutApi';
import { exerciseReadDTOToExerciseBlock } from '@/lib/transformers';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ExerciseListItem } from '@/components/overview/ExerciseListItem';
import { AddExerciseDialog } from '@/components/overview/AddExerciseDialog';
import { getSwapAlternatives } from '@/services/aiService';
import { Toast } from '@/components/ui/toast';

interface ManualWorkoutWizardProps {
  onComplete: (workout: Workout) => void;
  onCancel: () => void;
  onSwitchToAI: () => void;
}

type Step = 'basics' | 'exercises';

function SortableExerciseItem({
  exercise,
  index,
  onUpdate,
  onSwap,
  onToggleSuperset,
  isSuperset,
  onDelete,
  supersetGroupId,
  supersetGroupIndex,
  availableSupersetGroups,
  isHighlighted,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  isFirstInGroup,
  isLastInGroup,
}: {
  exercise: ExerciseBlock;
  index: number;
  onUpdate: (updates: Partial<ExerciseBlock>) => void;
  onSwap: () => void;
  onToggleSuperset: (groupId?: string) => void;
  isSuperset: boolean;
  onDelete?: () => void;
  supersetGroupId?: string;
  supersetGroupIndex?: number;
  availableSupersetGroups?: Array<{ id: string; letter: string; exerciseCount: number }>;
  isHighlighted?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ 
    id: exercise.id,
    // Only enable drag for superset items (internal reorder)
    disabled: !isSuperset,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Only pass drag handle props for superset items
  const dragHandleProps = isSuperset ? { attributes, listeners } : undefined;

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      animate={{
        scale: isHighlighted ? 1.02 : 1,
        borderColor: isHighlighted ? 'rgb(59, 130, 246)' : undefined,
      }}
      transition={{ duration: 0.3 }}
      className={isHighlighted ? 'rounded-lg border-2 border-blue-500 bg-blue-50/50' : ''}
    >
      <ExerciseListItem
        exercise={exercise}
        index={index}
        onUpdate={onUpdate}
        onSwap={onSwap}
        onToggleSuperset={onToggleSuperset}
        isSuperset={isSuperset}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onDelete={onDelete}
        supersetGroupId={supersetGroupId}
        supersetGroupIndex={supersetGroupIndex}
        availableSupersetGroups={availableSupersetGroups}
        dragHandleProps={dragHandleProps}
        isDragging={isDragging}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
      />
    </motion.div>
  );
}

export function ManualWorkoutWizard({ onComplete, onCancel, onSwitchToAI }: ManualWorkoutWizardProps) {
  const [step, setStep] = useState<Step>('basics');
  const [workoutName, setWorkoutName] = useState('');
  const [workoutDescription, setWorkoutDescription] = useState('');
  const [duration, setDuration] = useState<number>(30);
  const [exercises, setExercises] = useState<ExerciseBlock[]>([]);
  const [warmupCollapsed, setWarmupCollapsed] = useState(false);
  const [cooldownCollapsed, setCooldownCollapsed] = useState(false);
  const [swapExerciseId, setSwapExerciseId] = useState<string | null>(null);
  const [swapAlternatives, setSwapAlternatives] = useState<ExerciseBlock[]>([]);
  const [showAddExerciseDialog, setShowAddExerciseDialog] = useState(false);
  const [deleteConfirmExerciseId, setDeleteConfirmExerciseId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [highlightedExerciseId, setHighlightedExerciseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Categorize exercises by phase (warmup/cooldown/main based on phase field)
  const warmupExercises = useMemo(() => {
    return exercises.filter(
      (exercise) => exercise.phase === 'warmup'
    );
  }, [exercises]);

  const cooldownExercises = useMemo(() => {
    return exercises.filter(
      (exercise) => exercise.phase === 'cooldown'
    );
  }, [exercises]);

  const mainExercises = useMemo(() => {
    // All exercises that are not warmup or cooldown (default to main)
    return exercises.filter(
      (exercise) => !exercise.phase || exercise.phase === 'main'
    );
  }, [exercises]);

  const handleBasicsContinue = () => {
    if (!workoutName.trim()) {
      setError('Please enter a workout name');
      return;
    }
    if (duration < 1 || duration > 180) {
      setError('Duration must be between 1 and 180 minutes');
      return;
    }
    setError(null);
    setStep('exercises');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setExercises((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      
      const draggedItem = items[oldIndex];
      const targetItem = items[newIndex];
      
      // Only allow drag within superset groups
      if (draggedItem?.superset_group_id) {
        // Dragged item is in a superset - only allow drop within same group
        if (targetItem?.superset_group_id !== draggedItem.superset_group_id) {
          // Drop target is not in the same group - reject
          return items;
        }
      } else {
        // Non-superset items cannot be dragged (they use Up/Down buttons)
        return items;
      }
      
      const newItems = arrayMove(items, oldIndex, newIndex);
      
      // Update order values
      return newItems.map((item, index) => ({ ...item, order: index }));
    });
  };

  // Helper: Find the contiguous block containing an exercise (for superset-aware moves)
  const getExerciseBlock = (exerciseId: string, items: ExerciseBlock[]) => {
    const exercise = items.find(e => e.id === exerciseId);
    const exerciseIndex = items.findIndex(e => e.id === exerciseId);
    
    if (!exercise?.superset_group_id) {
      // Single exercise block
      return { start: exerciseIndex, end: exerciseIndex, groupId: null };
    }
    
    // Find contiguous range of this superset
    const groupId = exercise.superset_group_id;
    let start = exerciseIndex;
    let end = exerciseIndex;
    
    // Expand backwards
    while (start > 0 && items[start - 1].superset_group_id === groupId) {
      start--;
    }
    // Expand forwards
    while (end < items.length - 1 && items[end + 1].superset_group_id === groupId) {
      end++;
    }
    
    return { start, end, groupId };
  };

  // Helper: Get the previous block (for swap)
  const getPreviousBlock = (currentStart: number, items: ExerciseBlock[]) => {
    if (currentStart === 0) return null;
    
    const prevExercise = items[currentStart - 1];
    if (!prevExercise.superset_group_id) {
      return { start: currentStart - 1, end: currentStart - 1 };
    }
    
    // Find the start of the previous superset block
    const groupId = prevExercise.superset_group_id;
    let start = currentStart - 1;
    while (start > 0 && items[start - 1].superset_group_id === groupId) {
      start--;
    }
    return { start, end: currentStart - 1 };
  };

  // Helper: Get the next block (for swap)
  const getNextBlock = (currentEnd: number, items: ExerciseBlock[]) => {
    if (currentEnd >= items.length - 1) return null;
    
    const nextExercise = items[currentEnd + 1];
    if (!nextExercise.superset_group_id) {
      return { start: currentEnd + 1, end: currentEnd + 1 };
    }
    
    // Find the end of the next superset block
    const groupId = nextExercise.superset_group_id;
    let end = currentEnd + 1;
    while (end < items.length - 1 && items[end + 1].superset_group_id === groupId) {
      end++;
    }
    return { start: currentEnd + 1, end };
  };

  // Block-aware move: moves entire superset groups together
  const moveExercise = (exerciseId: string, direction: 'up' | 'down') => {
    setExercises((items) => {
      const exercise = items.find(e => e.id === exerciseId);
      if (!exercise) return items;
      
      // Find current block
      const currentBlock = getExerciseBlock(exerciseId, items);
      
      if (direction === 'up') {
        const prevBlock = getPreviousBlock(currentBlock.start, items);
        if (!prevBlock) return items;
        
        // Swap blocks: [prevBlock][currentBlock] -> [currentBlock][prevBlock]
        const before = items.slice(0, prevBlock.start);
        const prev = items.slice(prevBlock.start, prevBlock.end + 1);
        const current = items.slice(currentBlock.start, currentBlock.end + 1);
        const after = items.slice(currentBlock.end + 1);
        
        return [...before, ...current, ...prev, ...after].map((item, i) => ({ ...item, order: i }));
      } else {
        const nextBlock = getNextBlock(currentBlock.end, items);
        if (!nextBlock) return items;
        
        // Swap blocks: [currentBlock][nextBlock] -> [nextBlock][currentBlock]
        const before = items.slice(0, currentBlock.start);
        const current = items.slice(currentBlock.start, currentBlock.end + 1);
        const next = items.slice(nextBlock.start, nextBlock.end + 1);
        const after = items.slice(nextBlock.end + 1);
        
        return [...before, ...next, ...current, ...after].map((item, i) => ({ ...item, order: i }));
      }
    });
  };

  const handleUpdateExercise = (exerciseId: string, updates: Partial<ExerciseBlock>) => {
    setExercises((items) => {
      return items.map((item) => (item.id === exerciseId ? { ...item, ...updates } : item));
    });
  };

  const handleSwapClick = async (exerciseId: string) => {
    const updatedWorkout = { 
      id: `manual-${Date.now()}`,
      name: workoutName.trim(),
      description: workoutDescription.trim() || `A ${duration}-minute workout`,
      duration_minutes: duration,
      exercises,
      created_at: new Date().toISOString(),
      is_preset: false,
    };
    const alternatives = await getSwapAlternatives(exerciseId, updatedWorkout);
    setSwapExerciseId(exerciseId);
    setSwapAlternatives(alternatives);
  };

  const handleSwapSelect = (alternative: ExerciseBlock) => {
    if (!swapExerciseId) return;
    
    setExercises((items) => {
      return items.map((item) => (item.id === swapExerciseId ? alternative : item));
    });
    setSwapExerciseId(null);
    setSwapAlternatives([]);
  };

  const handleToggleSuperset = (exerciseId: string, groupId?: string) => {
    setExercises((items) => {
      return items.map((item) => {
        if (item.id === exerciseId) {
          if (groupId) {
            // Add to specific group
            return { ...item, superset_group_id: groupId };
          } else if (item.superset_group_id) {
            // Remove from superset
            return { ...item, superset_group_id: undefined };
          } else {
            // Create new group
            const newGroupId = `superset-${Date.now()}`;
            return { ...item, superset_group_id: newGroupId };
          }
        }
        return item;
      });
    });
  };

  const handleDeleteExercise = (exerciseId: string) => {
    setDeleteConfirmExerciseId(exerciseId);
  };

  const confirmDeleteExercise = () => {
    if (!deleteConfirmExerciseId) return;
    
    setExercises((items) => {
      const filtered = items.filter((item) => item.id !== deleteConfirmExerciseId);
      // Update order values after deletion
      return filtered.map((item, index) => ({ ...item, order: index }));
    });
    
    setDeleteConfirmExerciseId(null);
  };

  // Get all unique superset groups and their exercises
  const supersetGroups = useMemo(() => {
    const groups: Map<string, { exercises: ExerciseBlock[]; index: number }> = new Map();
    let groupIndex = 0;
    
    exercises.forEach((exercise) => {
      if (exercise.superset_group_id) {
        if (!groups.has(exercise.superset_group_id)) {
          groups.set(exercise.superset_group_id, { exercises: [], index: groupIndex++ });
        }
        groups.get(exercise.superset_group_id)!.exercises.push(exercise);
      }
    });
    
    return groups;
  }, [exercises]);

  const getSupersetGroup = (exerciseId: string) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    return exercise?.superset_group_id;
  };

  // Check if an exercise is the first or last in its superset group (for visual styling)
  const getGroupPosition = (exerciseId: string) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    if (!exercise?.superset_group_id) {
      return { isFirstInGroup: false, isLastInGroup: false };
    }
    
    const exerciseIndex = exercises.findIndex(e => e.id === exerciseId);
    const groupId = exercise.superset_group_id;
    
    // Check if this is the first exercise in the group (no previous item with same groupId)
    const isFirstInGroup = exerciseIndex === 0 || 
      exercises[exerciseIndex - 1].superset_group_id !== groupId;
    
    // Check if this is the last exercise in the group (no next item with same groupId)
    const isLastInGroup = exerciseIndex === exercises.length - 1 || 
      exercises[exerciseIndex + 1].superset_group_id !== groupId;
    
    return { isFirstInGroup, isLastInGroup };
  };

  const getSupersetGroupInfo = (exerciseId: string) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    if (!exercise?.superset_group_id) return null;
    
    const group = supersetGroups.get(exercise.superset_group_id);
    if (!group) return null;
    
    return {
      id: exercise.superset_group_id,
      index: group.index,
    };
  };

  // Get available superset groups for selector (excluding current exercise's group)
  const getAvailableSupersetGroups = (currentExerciseId: string) => {
    const currentExercise = exercises.find((e) => e.id === currentExerciseId);
    const availableGroups: Array<{ id: string; letter: string; exerciseCount: number }> = [];
    
    supersetGroups.forEach((group, groupId) => {
      // Exclude the group this exercise is already in
      if (currentExercise?.superset_group_id !== groupId) {
        const letter = String.fromCharCode(65 + group.index);
        availableGroups.push({
          id: groupId,
          letter,
          exerciseCount: group.exercises.length,
        });
      }
    });
    
    return availableGroups;
  };

  const handleAddExercise = (newExercise: ExerciseBlock) => {
    // Determine which section to add to based on phase (default to main)
    let phase = newExercise.phase || 'main';
    const exerciseWithPhase = { ...newExercise, phase };
    
    // Add to the end of the appropriate section
    setExercises((items) => {
      const newItems = [...items, { ...exerciseWithPhase, order: items.length }];
      const reordered = newItems.map((item, index) => ({ ...item, order: index }));
      
      // Highlight the newly added exercise (use the new exercise's ID)
      setHighlightedExerciseId(exerciseWithPhase.id);
      
      // Clear highlight after 2 seconds
      setTimeout(() => {
        setHighlightedExerciseId(null);
      }, 2000);
      
      return reordered;
    });
  };

  const handleExerciseAdded = (exerciseName: string) => {
    // Set toast message
    setToastMessage(`Added ${exerciseName}`);
    
    // Clear toast after 3 seconds
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const handleComplete = async () => {
    if (exercises.length === 0) {
      setError('Please add at least one exercise');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setError(null);
    
    try {
      const workout: Workout = {
        id: `manual-${Date.now()}`, // Temporary ID, will be replaced
        name: workoutName.trim(),
        description: workoutDescription.trim() || `A ${duration}-minute workout`,
        duration_minutes: duration,
        exercises,
        created_at: new Date().toISOString(),
        is_preset: false,
      };

      // Save workout via API
      const savedWorkout = await workoutApi.createWorkout(workout);
      
      // Pass the saved workout (with real server ID) to onComplete
      onComplete(savedWorkout);
    } catch (error) {
      console.error('Failed to save workout:', error);
      setSaveError('Failed to save workout. Please try again.');
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (step === 'exercises') {
      setStep('basics');
    } else {
      onCancel();
    }
  };

  const getStepNumber = () => (step === 'basics' ? 1 : 2);
  const getProgressWidth = () => `${(getStepNumber() / 2) * 100}%`;

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
      <div className="w-full max-w-2xl">
        {/* Progress indicator */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-neutral-500">Step {getStepNumber()} of 2</span>
            <button
              onClick={onSwitchToAI}
              className="text-sm text-neutral-600 hover:text-neutral-900 underline transition-colors"
            >
              Switch to AI Mode
            </button>
          </div>
          <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-neutral-900"
              initial={{ width: 0 }}
              animate={{ width: getProgressWidth() }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Basics */}
          {step === 'basics' && (
            <motion.div
              key="basics"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">Create your workout</h2>
              <p className="text-neutral-600 mb-8">Start with the basics.</p>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Workout Name *
                  </label>
                  <Input
                    type="text"
                    value={workoutName}
                    onChange={(e) => setWorkoutName(e.target.value)}
                    placeholder="e.g., Morning Strength Session"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Description (optional)
                  </label>
                  <Input
                    type="text"
                    value={workoutDescription}
                    onChange={(e) => setWorkoutDescription(e.target.value)}
                    placeholder="e.g., Full body workout focusing on compound movements"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Duration (minutes) *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="180"
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
                    className="w-full"
                  />
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleBasicsContinue}
                className="w-full mt-8 py-5 px-6 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-all text-lg font-medium"
              >
                Continue to Exercises
              </button>
            </motion.div>
          )}

          {/* Step 2: Exercises */}
          {step === 'exercises' && (
            <motion.div
              key="exercises"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-4xl mb-3 text-neutral-900">Add exercises</h2>
              <p className="text-neutral-600 mb-8">
                Build your {workoutName} workout by adding exercises.
              </p>

              {/* Exercise list with detailed edit mode */}
              <div className="mb-6">
                {exercises.length === 0 ? (
                  <div className="text-center py-12 bg-white border border-neutral-200 rounded-lg">
                    <p className="text-neutral-500 mb-4">No exercises added yet</p>
                    <button
                      onClick={() => setShowAddExerciseDialog(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      Add your first exercise
                    </button>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={exercises.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-0">
                        {/* Warmup Section */}
                        {warmupExercises.length > 0 && (
                          <div>
                            <button
                              onClick={() => setWarmupCollapsed(!warmupCollapsed)}
                              className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-3 transition-colors"
                            >
                              {warmupCollapsed ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronUp className="w-4 h-4" />
                              )}
                              <span className="text-sm font-medium">Warmup</span>
                            </button>
                            {!warmupCollapsed && (
                              <div className="space-y-0">
                                {warmupExercises.map((exercise, index) => {
                                  const groupInfo = getSupersetGroupInfo(exercise.id);
                                  const block = getExerciseBlock(exercise.id, exercises);
                                  const { isFirstInGroup, isLastInGroup } = getGroupPosition(exercise.id);
                                  return (
                                    <div key={exercise.id}>
                                      <SortableExerciseItem
                                        exercise={exercise}
                                        index={index}
                                        onUpdate={(updates) => handleUpdateExercise(exercise.id, updates)}
                                        onSwap={() => handleSwapClick(exercise.id)}
                                        onToggleSuperset={(groupId) => handleToggleSuperset(exercise.id, groupId)}
                                        isSuperset={!!getSupersetGroup(exercise.id)}
                                        onDelete={() => handleDeleteExercise(exercise.id)}
                                        supersetGroupId={exercise.superset_group_id}
                                        supersetGroupIndex={groupInfo?.index}
                                        availableSupersetGroups={getAvailableSupersetGroups(exercise.id)}
                                        isHighlighted={highlightedExerciseId === exercise.id}
                                        onMoveUp={() => moveExercise(exercise.id, 'up')}
                                        onMoveDown={() => moveExercise(exercise.id, 'down')}
                                        canMoveUp={block.start > 0}
                                        canMoveDown={block.end < exercises.length - 1}
                                        isFirstInGroup={isFirstInGroup}
                                        isLastInGroup={isLastInGroup}
                                      />
                                      {index < warmupExercises.length - 1 && (
                                        <div className="relative my-3">
                                          <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-neutral-200"></div>
                                          </div>
                                          <div className="relative flex justify-center">
                                            <button
                                              onClick={() => setShowAddExerciseDialog(true)}
                                              className="bg-white w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                                            >
                                              <Plus className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Separator between Warmup and Main */}
                        {warmupExercises.length > 0 && mainExercises.length > 0 && (
                          <div className="relative my-3">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-neutral-200"></div>
                            </div>
                            <div className="relative flex justify-center">
                              <button
                                onClick={() => setShowAddExerciseDialog(true)}
                                className="bg-neutral-50 w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Main Exercises */}
                        {mainExercises.length > 0 && (
                          <div className="space-y-0">
                            {mainExercises.map((exercise, index) => {
                              const groupInfo = getSupersetGroupInfo(exercise.id);
                              const block = getExerciseBlock(exercise.id, exercises);
                              const { isFirstInGroup, isLastInGroup } = getGroupPosition(exercise.id);
                              return (
                                <div key={exercise.id}>
                                  <SortableExerciseItem
                                    exercise={exercise}
                                    index={warmupExercises.length + index}
                                    onUpdate={(updates) => handleUpdateExercise(exercise.id, updates)}
                                    onSwap={() => handleSwapClick(exercise.id)}
                                    onToggleSuperset={(groupId) => handleToggleSuperset(exercise.id, groupId)}
                                    isSuperset={!!getSupersetGroup(exercise.id)}
                                    onDelete={() => handleDeleteExercise(exercise.id)}
                                    supersetGroupId={exercise.superset_group_id}
                                    supersetGroupIndex={groupInfo?.index}
                                    availableSupersetGroups={getAvailableSupersetGroups(exercise.id)}
                                    isHighlighted={highlightedExerciseId === exercise.id}
                                    onMoveUp={() => moveExercise(exercise.id, 'up')}
                                    onMoveDown={() => moveExercise(exercise.id, 'down')}
                                    canMoveUp={block.start > 0}
                                    canMoveDown={block.end < exercises.length - 1}
                                    isFirstInGroup={isFirstInGroup}
                                    isLastInGroup={isLastInGroup}
                                  />
                                  {index < mainExercises.length - 1 && (
                                    <div className="relative my-3">
                                      <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-neutral-200"></div>
                                      </div>
                                      <div className="relative flex justify-center">
                                        <button
                                          onClick={() => setShowAddExerciseDialog(true)}
                                          className="bg-white w-8 h-8 rounded-full border-2 border-dashed border-neutral-300 hover:border-neutral-400 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors"
                                        >
                                          <Plus className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Separator between Main and Cooldown */}
                        {mainExercises.length > 0 && cooldownExercises.length > 0 && (
                          <div className="relative my-3">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-neutral-200"></div>
                            </div>
                            <div className="relative flex justify-center">
                              <button
                                onClick={() => setShowAddExerciseDialog(true)}
                                className="bg-neutral-50 w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Cooldown Section */}
                        {cooldownExercises.length > 0 && (
                          <div>
                            <button
                              onClick={() => setCooldownCollapsed(!cooldownCollapsed)}
                              className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-3 transition-colors"
                            >
                              {cooldownCollapsed ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronUp className="w-4 h-4" />
                              )}
                              <span className="text-sm font-medium">Cooldown</span>
                            </button>
                            {!cooldownCollapsed && (
                              <div className="space-y-0">
                                {cooldownExercises.map((exercise, index) => {
                                  const groupInfo = getSupersetGroupInfo(exercise.id);
                                  const block = getExerciseBlock(exercise.id, exercises);
                                  const { isFirstInGroup, isLastInGroup } = getGroupPosition(exercise.id);
                                  return (
                                    <div key={exercise.id}>
                                      <SortableExerciseItem
                                        exercise={exercise}
                                        index={exercises.length - cooldownExercises.length + index}
                                        onUpdate={(updates) => handleUpdateExercise(exercise.id, updates)}
                                        onSwap={() => handleSwapClick(exercise.id)}
                                        onToggleSuperset={(groupId) => handleToggleSuperset(exercise.id, groupId)}
                                        isSuperset={!!getSupersetGroup(exercise.id)}
                                        onDelete={() => handleDeleteExercise(exercise.id)}
                                        supersetGroupId={exercise.superset_group_id}
                                        supersetGroupIndex={groupInfo?.index}
                                        availableSupersetGroups={getAvailableSupersetGroups(exercise.id)}
                                        isHighlighted={highlightedExerciseId === exercise.id}
                                        onMoveUp={() => moveExercise(exercise.id, 'up')}
                                        onMoveDown={() => moveExercise(exercise.id, 'down')}
                                        canMoveUp={block.start > 0}
                                        canMoveDown={block.end < exercises.length - 1}
                                        isFirstInGroup={isFirstInGroup}
                                        isLastInGroup={isLastInGroup}
                                      />
                                      {index < cooldownExercises.length - 1 && (
                                        <div className="relative my-3">
                                          <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-neutral-200"></div>
                                          </div>
                                          <div className="relative flex justify-center">
                                            <button
                                              onClick={() => setShowAddExerciseDialog(true)}
                                              className="bg-white w-7 h-7 rounded-full border border-dashed border-neutral-200 hover:border-neutral-300 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition-colors"
                                            >
                                              <Plus className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Add exercise button at the end */}
                        {exercises.length > 0 && (
                          <div className="relative my-3">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-neutral-200"></div>
                            </div>
                            <div className="relative flex justify-center">
                              <button
                                onClick={() => setShowAddExerciseDialog(true)}
                                className="bg-white w-8 h-8 rounded-full border-2 border-dashed border-neutral-300 hover:border-neutral-400 flex items-center justify-center text-neutral-400 hover:text-neutral-600 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {saveError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {saveError}
                </div>
              )}

              <button
                onClick={handleComplete}
                disabled={exercises.length === 0 || isSaving}
                className="w-full mt-8 py-5 px-6 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 transition-all text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : `Create Workout (${exercises.length} exercise${exercises.length !== 1 ? 's' : ''})`}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 'basics' ? 'Cancel' : 'Back'}
          </button>
        </div>

        {/* Add Exercise Dialog */}
        <AddExerciseDialog
          isOpen={showAddExerciseDialog}
          onClose={() => setShowAddExerciseDialog(false)}
          onAdd={handleAddExercise}
          currentExerciseCount={exercises.length}
          onExerciseAdded={handleExerciseAdded}
        />

        {/* Toast Notification */}
        <AnimatePresence>
          {toastMessage && (
            <Toast
              message={toastMessage}
              duration={3000}
              onClose={() => setToastMessage(null)}
            />
          )}
        </AnimatePresence>

        {/* Swap Alternatives Dialog */}
        {swapExerciseId && swapAlternatives.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Swap Exercise</h3>
                <button
                  onClick={() => {
                    setSwapExerciseId(null);
                    setSwapAlternatives([]);
                  }}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-neutral-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="space-y-2">
                  {swapAlternatives.map((alternative) => (
                    <button
                      key={alternative.id}
                      onClick={() => handleSwapSelect(alternative)}
                      className="w-full text-left p-4 border border-neutral-200 rounded-lg hover:border-neutral-400 hover:bg-neutral-50 transition-all"
                    >
                      <div className="font-medium text-neutral-900">{alternative.name}</div>
                      {alternative.instruction && (
                        <div className="text-sm text-neutral-600 mt-1">
                          {alternative.instruction}
                        </div>
                      )}
                      <div className="text-sm text-neutral-500 mt-2">
                        {alternative.target.mode === 'reps'
                          ? `${alternative.target.sets} sets × ${alternative.target.reps} reps`
                          : `${alternative.target.duration_seconds}s`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end border-t pt-4 mt-4">
                <Button
                  onClick={() => {
                    setSwapExerciseId(null);
                    setSwapAlternatives([]);
                  }}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirmExerciseId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-xl font-semibold mb-4">Delete Exercise</h3>
              <p className="text-neutral-600 mb-6">
                Are you sure you want to remove this exercise from your workout?
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  onClick={() => setDeleteConfirmExerciseId(null)}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteExercise}
                  variant="default"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
